import { useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Archive,
  ArrowLeft,
  CheckCircle2,
  Download,
  Forward,
  Loader2,
  Reply,
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useActiveClinic } from '@/lib/activeClinic';
import { useAuth } from '@/lib/auth';
import {
  useArchiveMedcomImport,
  useBundleJson,
  useCareCommunicationThread,
  useCareCommunicationsList,
  useExportCareCommunication,
  useMessageAcknowledgements,
  useThreadLetterBundles,
  type CareCommunicationListFilters,
  type CareCommunicationListItem,
  type ThreadSegment,
} from '@/hooks/useCareCommunications';
import { letterParty, patientIdentityBadgeLabel } from '@/lib/medcom/letterDisplay';
import { Button, ButtonLink } from '@/components/ui/Button';
import { CareCommunicationPriorityChip } from '@/components/medcom/CareCommunicationPriorityChip';
import { OrganizationName } from '@/components/medcom/OrganizationName';
import { LetterTitle } from '@/pages/communicate/LetterColumns';
import { ErrorAlert } from '@/components/ui/ErrorAlert';
import { EntityTasksPanel } from '@/components/tasks/EntityTasksPanel';
import { useCareCommunicationReads } from '@/hooks/useCareCommunicationReads';
import { episodeOfCareLabel } from '@/lib/medcom/parseCareCommunicationBundle';
import {
  careCommunicationForwardPath,
  fillThreadSegmentSendersFromActive,
  openedThreadMessage,
  owningThreadMessageId,
  threadHeading,
  threadSegmentBody,
} from '@/lib/medcom/threadDisplay';
import { visibleThreadMessages, visibleThreadSegments } from '@/lib/medcom/threadMessages';
import { CareCommunicationLetterDetails } from '@/pages/medcom/CareCommunicationLetterDetails';
import {
  receiptsForLetter,
  resendForOriginal,
  transmissionLabel,
  visibleTransmissions,
} from '@/pages/medcom/acknowledgementDisplay';
import { useAcknowledgementOutcomeTexts } from '@/pages/medcom/useAcknowledgementOutcomes';
import { inboxStatusLabel } from '@/pages/medcom/MedComInbox';
import { AckBadge, StatusBadge } from './CareCommunications';

export function CareCommunicationThread() {
  const { communicationIdentifier: rawId } = useParams<{
    communicationIdentifier: string;
  }>();
  const [searchParams] = useSearchParams();
  const requestedMessageId = searchParams.get('messageId');
  const communicationIdentifier = rawId ? decodeURIComponent(rawId) : undefined;
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { activeClinicId } = useActiveClinic();
  const { isPlatformAdmin } = useAuth();
  const archiveImport = useArchiveMedcomImport();

  const { data, isLoading, error } = useCareCommunicationThread(
    activeClinicId,
    communicationIdentifier,
  );
  const exportMutation = useExportCareCommunication();
  const { markRead } = useCareCommunicationReads(activeClinicId);
  const openedMessageId = openedThreadMessage(data?.messages ?? [], requestedMessageId)?.id;
  const { data: openedBundle, isLoading: openedLetterLoading } = useBundleJson(
    activeClinicId,
    openedMessageId,
  );
  const inboundIds = (data?.messages ?? [])
    .filter((item) => item.direction === 'in')
    .map((item) => item.id)
    .join('\n');
  const threadLetterIds = (data?.messages ?? []).map((item) => item.id);
  const { data: threadLetters, isLoading: lettersLoading } = useThreadLetterBundles(
    activeClinicId,
    threadLetterIds,
  );

  useEffect(() => {
    if (communicationIdentifier) markRead(communicationIdentifier);
    if (!inboundIds) return;
    for (const id of inboundIds.split('\n')) markRead(id);
    for (const item of data?.messages ?? []) {
      if (item.direction !== 'in') continue;
      markRead(item.communicationIdentifier);
    }
  }, [communicationIdentifier, inboundIds, data?.messages, markRead]);

  if (isLoading || (threadLetterIds.length > 0 && lettersLoading)) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-ds-muted" />
      </div>
    );
  }

  if (error || !data || !communicationIdentifier) {
    return <ErrorAlert message={t('communicate.error_not_found')} />;
  }

  const assembled = data.assembled;
  const visibleMessages = visibleThreadMessages(data.messages);
  const segments = fillThreadSegmentSendersFromActive(
    visibleThreadSegments(assembled?.segments ?? [], visibleMessages),
    visibleMessages,
    threadLetters ?? new Map(),
  );
  const openedMessage = openedThreadMessage(data.messages, requestedMessageId);
  const heading = threadHeading({
    selected: openedMessage,
    selectedLetter: openedBundle
      ? { topic: openedBundle.topic, category: openedBundle.categoryCode }
      : null,
    messages: visibleMessages,
  });
  const identityBadge = patientIdentityBadgeLabel({
    cpr: openedBundle?.cpr ?? assembled?.patient?.cpr,
    xEcpr: openedBundle?.xEcpr ?? assembled?.patient?.xEcpr,
    dEcpr: openedBundle?.dEcpr ?? assembled?.patient?.dEcpr,
  });
  const patientName = [assembled?.patient?.given?.join(' '), assembled?.patient?.family]
    .filter(Boolean)
    .join(' ')
    .trim();

  async function handleExport(
    messageId: string,
    format: 'json' | 'xml',
    acknowledgementId?: string,
  ) {
    if (!activeClinicId) return;
    try {
      await exportMutation.mutateAsync({
        clinicId: activeClinicId,
        messageId,
        format,
        acknowledgementId,
      });
      toast.success(t('communicate.toast_exported'));
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : t('communicate.errors.export_failed'),
      );
    }
  }

  async function handleArchiveOpened() {
    if (!activeClinicId || !openedMessage || !isPlatformAdmin) return;
    if (!window.confirm(t('communicate.archive_confirm'))) return;
    try {
      await archiveImport.mutateAsync({
        clinicId: activeClinicId,
        messageId: openedMessage.id,
        bundleId: openedMessage.bundleId,
      });
      toast.success(t('communicate.toast_archived'));
      navigate('/medcom/letters');
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : t('communicate.errors.archive_failed'),
      );
    }
  }

  return (
    <div>
      <div>
          <button
            type="button"
            onClick={() => navigate('/medcom/letters')}
            className="mb-4 inline-flex items-center text-sm text-ds-muted hover:text-ds-text"
          >
            <ArrowLeft className="mr-1 h-4 w-4" />
            {t('communicate.back_to_list')}
          </button>

          <div className="md:flex md:items-start md:justify-between">
            <div className="min-w-0 flex-1">
              <LetterTitle
                category={heading.category}
                topic={
                  heading.topic ||
                  (openedLetterLoading ? null : data.thread?.subject?.trim()) ||
                  null
                }
                fallback={t('communicate.thread_title')}
              />
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <CareCommunicationPriorityChip
                  priority={openedBundle?.priority}
                  variant="compact"
                />
                {identityBadge ? (
                  <span className="inline-flex items-center rounded-full bg-ds-green-tint px-2 py-0.5 text-xs font-medium text-ds-green-ink">
                    <CheckCircle2 className="mr-1 h-3 w-3" />
                    {identityBadge}
                  </span>
                ) : null}
                {patientName ? (
                  <span className="text-sm text-ds-muted">
                    {patientName}
                  </span>
                ) : null}
                {data.thread?.status ? (
                  <span className="text-xs text-ds-muted">
                    {t(`communicate.thread_status_${data.thread.status}`, {
                      defaultValue: data.thread.status,
                    })}
                  </span>
                ) : null}
              </div>
            </div>
            <div className="mt-4 flex flex-wrap gap-2 md:ml-4 md:mt-0">
              <Button
                variant="secondary"
                onClick={() =>
                  navigate(
                    openedMessage
                      ? `/medcom/letters/${encodeURIComponent(communicationIdentifier)}/reply?messageId=${encodeURIComponent(openedMessage.id)}`
                      : `/medcom/letters/${encodeURIComponent(communicationIdentifier)}/reply`,
                  )
                }
              >
                <Reply className="mr-1 h-4 w-4" />
                {t('communicate.reply')}
              </Button>
              <ButtonLink
                variant="secondary"
                to={careCommunicationForwardPath(communicationIdentifier, openedMessage?.id)}
              >
                <Forward className="mr-1 h-4 w-4" />
                {t('communicate.forward')}
              </ButtonLink>
              {isPlatformAdmin && openedMessage ? (
                <Button
                  variant="danger"
                  disabled={archiveImport.isPending}
                  onClick={() => {
                    void handleArchiveOpened();
                  }}
                >
                  <Archive className="mr-1 h-4 w-4" />
                  {t('communicate.archive_import')}
                </Button>
              ) : null}
            </div>
          </div>

          {activeClinicId && communicationIdentifier ? (
            <div className="mt-s5">
              <EntityTasksPanel
                clinicId={activeClinicId}
                entityType="letter"
                entityId={communicationIdentifier}
              />
            </div>
          ) : null}

          {activeClinicId && openedMessage ? (
            <OpenedLetter
              clinicId={activeClinicId}
              message={openedMessage}
              onExportAcknowledgement={(acknowledgementId, format) => {
                void handleExport(openedMessage.id, format, acknowledgementId);
              }}
              onExportMessage={(messageId, format) => {
                void handleExport(messageId, format);
              }}
            />
          ) : null}

          {/* Ordered assembled segments */}
          <section className="mt-8">
            <h3 className="text-sm font-semibold text-ds-text">
              {t('communicate.section_segments')}
            </h3>
            {segments.length === 0 ? (
              <p className="mt-3 text-sm text-ds-muted">
                {t('communicate.no_segments')}
              </p>
            ) : (
              <ul className="mt-4 space-y-4">
                {segments.map((seg) => {
                  const messageId = owningThreadMessageId(seg, visibleMessages);
                  return (
                    <SegmentCard
                      key={seg.segmentId}
                      segment={seg}
                      forwardTo={
                        messageId
                          ? careCommunicationForwardPath(communicationIdentifier, messageId)
                          : null
                      }
                    />
                  );
                })}
              </ul>
            )}
          </section>

          {/* Message list with ack + export */}
          <section className="mt-10">
            <h3 className="text-sm font-semibold text-ds-text">
              {t('communicate.section_messages')}
            </h3>
            {visibleMessages.length === 0 ? (
              <p className="mt-3 text-sm text-ds-muted">
                {t('communicate.no_messages')}
              </p>
            ) : (
              <ul className="mt-4 divide-y divide-ds-line rounded-md border border-ds-line bg-ds-card">
                {visibleMessages.map((msg) => (
                  <MessageRow
                    key={msg.id}
                    clinicId={activeClinicId}
                    threadIdentifier={communicationIdentifier}
                    item={msg}
                    exporting={exportMutation.isPending}
                    onExportJson={() => void handleExport(msg.id, 'json')}
                    onExportXml={() => void handleExport(msg.id, 'xml')}
                    onExportAckJson={
                      msg.latestAcknowledgement
                        ? () => void handleExport(msg.id, 'json', msg.latestAcknowledgement?.id)
                        : undefined
                    }
                    onExportAckXml={
                      msg.latestAcknowledgement
                        ? () => void handleExport(msg.id, 'xml', msg.latestAcknowledgement?.id)
                        : undefined
                    }
                  />
                ))}
              </ul>
            )}
          </section>
        </div>
    </div>
  );
}

const OUTBOUND_LIST_FILTERS: CareCommunicationListFilters = {
  direction: 'out',
  status: 'all',
};

function OpenedLetter({
  clinicId,
  message,
  onExportAcknowledgement,
  onExportMessage,
}: {
  clinicId: string;
  message: CareCommunicationListItem;
  onExportAcknowledgement?: (acknowledgementId: string, format: 'json' | 'xml') => void;
  onExportMessage?: (messageId: string, format: 'json' | 'xml') => void;
}) {
  const { t } = useTranslation();
  const { data: bundle, isLoading } = useBundleJson(clinicId, message.id);
  const { data: storedAcks } = useMessageAcknowledgements(clinicId, message.id);
  const { data: outbound } = useCareCommunicationsList(clinicId, OUTBOUND_LIST_FILTERS);
  const ackSources = storedAcks?.acknowledgements ?? (
    message.latestAcknowledgement
      ? [{
          ...message.latestAcknowledgement,
          duplicateResend: message.acknowledgementCount > 1,
        }]
      : []
  );
  const { data: outcomeTexts } = useAcknowledgementOutcomeTexts(
    clinicId,
    message.id,
    ackSources,
  );
  if (isLoading || !bundle) return null;
  const resend = resendForOriginal(message.id, message.resend, outbound?.items ?? []);
  const receipts = receiptsForLetter(ackSources, outcomeTexts, resend);
  return (
    <section className="mt-8">
      <CareCommunicationLetterDetails
        bundle={bundle}
        receivedAt={message.receivedAt ?? message.createdAt}
        status={message.status}
        statusLabel={receipts.status?.label ?? inboxStatusLabel(message.status)}
        statusTone={receipts.status?.tone}
        acknowledgements={receipts.cards}
        ackTimeoutWarning={
          message.ackTimeoutExhausted ? t('communicate.ack_timeout_exhausted') : null
        }
        onExportAcknowledgement={onExportAcknowledgement}
        onExportMessage={onExportMessage}
      />
    </section>
  );
}

function LetterFacts({ clinicId, messageId }: { clinicId: string; messageId: string }) {
  const { data: bundle } = useBundleJson(clinicId, messageId);
  if (!bundle) return null;

  const rows: Array<{ label: string; value: string }> = [];
  if (bundle.cpr) rows.push({ label: 'CPR', value: bundle.cpr });
  if (bundle.xEcpr) rows.push({ label: 'Erstatnings-CPR (X)', value: bundle.xEcpr });
  if (bundle.dEcpr) rows.push({ label: 'Erstatnings-CPR (D)', value: bundle.dEcpr });
  if (bundle.episodeOfCareIdentifiers.length > 0) {
    for (const episode of bundle.episodeOfCareIdentifiers) {
      rows.push({ label: episodeOfCareLabel(episode.source), value: episode.value });
    }
  } else if (bundle.episodeOfCareId) {
    rows.push({ label: 'Forløbs-ID', value: bundle.episodeOfCareId });
  }
  if (bundle.senderRole) rows.push({ label: 'Titel', value: bundle.senderRole });
  if (bundle.senderPhone) rows.push({ label: 'Telefon', value: bundle.senderPhone });
  if (rows.length === 0) return null;

  return (
    <dl className="mt-2 grid grid-cols-1 gap-x-6 gap-y-1 sm:grid-cols-2">
      {rows.map((row) => (
        <div key={`${row.label}:${row.value}`}>
          <dt className="text-xs text-ds-muted">{row.label}</dt>
          <dd className="text-sm text-ds-text">{row.value}</dd>
        </div>
      ))}
    </dl>
  );
}

function SegmentCard({
  segment,
  forwardTo,
}: {
  segment: ThreadSegment;
  forwardTo: string | null;
}) {
  const { t } = useTranslation();
  return (
    <li className="rounded-md border border-ds-line bg-ds-card p-4">
      <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-ds-muted">
        <span>
          {new Date(segment.timestamp).toLocaleString('da-DK')}
          {segment.activityCode
            ? ` · ${t(`communicate.activity_${segment.activityCode}`, {
                defaultValue: segment.activityCode,
              })}`
            : ''}
        </span>
        <span className="flex flex-wrap items-center gap-2">
          <span>
            <OrganizationName
              name={segment.sender.organizationName}
              sorId={segment.sender.sorId}
              fallback="—"
            />
            {segment.sender.practitionerName
              ? ` · ${segment.sender.practitionerName}`
              : ''}
          </span>
          {forwardTo ? (
            <ButtonLink variant="secondary" size="sm" to={forwardTo}>
              <Forward className="mr-1 h-3.5 w-3.5" />
              {t('communicate.forward')}
            </ButtonLink>
          ) : null}
        </span>
      </div>
      <SegmentSummary segment={segment} />
    </li>
  );
}

function SegmentSummary({ segment }: { segment: ThreadSegment }) {
  const { t } = useTranslation();
  switch (segment.kind) {
    case 'string': {
      const text = threadSegmentBody(segment, null);
      if (!text) {
        return (
          <p className="mt-2 text-sm text-ds-muted">
            {t('communicate.segment_text')}
          </p>
        );
      }
      return (
        <p className="mt-2 whitespace-pre-wrap text-sm text-ds-text">{text}</p>
      );
    }
    case 'attachment':
      return (
        <p className="mt-2 text-sm text-ds-text">
          {t('communicate.attachment_label')}: {segment.attachment?.title || '—'}
        </p>
      );
    default: {
      const unexpected: never = segment.kind;
      void unexpected;
      return null;
    }
  }
}

function OutboundTransmissionList({
  transmissions,
}: {
  transmissions: CareCommunicationListItem['transmissions'];
}) {
  const { t } = useTranslation();
  const lines = visibleTransmissions(transmissions);
  if (lines.length === 0) return null;
  return (
    <ul className="mt-1 space-y-0.5 text-xs text-ds-muted">
      {lines.map((line) => (
        <li key={`${line.kind}-${line.attempt}-${line.sentAt}`}>
          {transmissionLabel(line, (key, options) => t(key, options))}
        </li>
      ))}
    </ul>
  );
}

function MessageRow({
  clinicId,
  threadIdentifier,
  item,
  exporting,
  onExportJson,
  onExportXml,
  onExportAckJson,
  onExportAckXml,
}: {
  clinicId: string | null;
  threadIdentifier: string;
  item: CareCommunicationListItem;
  exporting: boolean;
  onExportJson: () => void;
  onExportXml: () => void;
  onExportAckJson?: () => void;
  onExportAckXml?: () => void;
}) {
  const { t } = useTranslation();
  const { data: bundle } = useBundleJson(clinicId, item.id);
  const sender = letterParty(
    { name: item.senderOrganization, sorId: item.senderSor },
    bundle
      ? { name: bundle.senderOrganization, sorId: bundle.senderSor }
      : null,
  );
  const receiver = letterParty(
    { name: item.receiverOrganization, sorId: item.receiverSor },
    bundle
      ? { name: bundle.receiverOrganization, sorId: bundle.receiverSor }
      : null,
  );
  const dateIso =
    item.direction === 'in'
      ? item.receivedAt ?? item.createdAt
      : item.sentAt ?? item.createdAt;

  return (
    <li className="px-4 py-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 space-y-1">
          <div className="flex flex-wrap items-center gap-2">
            <span
              className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
                item.direction === 'in'
                  ? 'bg-ds-teal-tint text-ds-teal'
                  : 'bg-ds-neutral-tint text-ds-muted'
              }`}
            >
              {t(`communicate.direction_${item.direction}`)}
            </span>
            <StatusBadge status={item.status} />
            <AckBadge item={item} />
            <CareCommunicationPriorityChip
              priority={bundle?.priority ?? item.priority}
              variant="compact"
            />
          </div>
          <p className="text-sm text-ds-muted">
            {t('communicate.col_sender')}: <OrganizationName name={sender.name} sorId={sender.sorId} fallback="—" /> →{' '}
            {t('communicate.col_receiver')}: <OrganizationName
              name={receiver.name}
              sorId={receiver.sorId}
              fallback="—"
            />
          </p>
          <p className="text-xs text-ds-muted">
            {dateIso ? new Date(dateIso).toLocaleString('da-DK') : '—'}
          </p>
          {item.direction === 'out' ? (
            <OutboundTransmissionList transmissions={item.transmissions} />
          ) : null}
          {item.latestAcknowledgement?.errorMessage ? (
            <p className="text-sm text-ds-red-ink">
              {item.latestAcknowledgement.errorMessage}
            </p>
          ) : null}
          {clinicId ? <LetterFacts clinicId={clinicId} messageId={item.id} /> : null}
        </div>
        <div className="flex flex-wrap gap-2">
          <ButtonLink
            variant="secondary"
            size="sm"
            to={careCommunicationForwardPath(threadIdentifier, item.id)}
          >
            <Forward className="mr-1 h-3.5 w-3.5" />
            {t('communicate.forward')}
          </ButtonLink>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            disabled={exporting}
            onClick={onExportJson}
          >
            <Download className="mr-1 h-3.5 w-3.5" />
            {t('communicate.export_json')}
          </Button>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            disabled={exporting}
            onClick={onExportXml}
          >
            <Download className="mr-1 h-3.5 w-3.5" />
            {t('communicate.export_xml')}
          </Button>
          {onExportAckJson ? (
            <Button
              type="button"
              variant="secondary"
              size="sm"
              disabled={exporting}
              onClick={onExportAckJson}
            >
              <Download className="mr-1 h-3.5 w-3.5" />
              {t('communicate.ack_export_json')}
            </Button>
          ) : null}
          {onExportAckXml ? (
            <Button
              type="button"
              variant="secondary"
              size="sm"
              disabled={exporting}
              onClick={onExportAckXml}
            >
              <Download className="mr-1 h-3.5 w-3.5" />
              {t('communicate.ack_export_xml')}
            </Button>
          ) : null}
        </div>
      </div>
    </li>
  );
}
